import { config, TOKEN_STORAGE_KEY } from '../config';

export const getAuthToken = (): string | null =>
  localStorage.getItem(TOKEN_STORAGE_KEY);

export const setAuthToken = (token: string): void =>
  localStorage.setItem(TOKEN_STORAGE_KEY, token);

export const clearAuthToken = (): void =>
  localStorage.removeItem(TOKEN_STORAGE_KEY);

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Extra query params appended to the URL. */
  params?: Record<string, unknown>;
  /** Skip attaching the Authorization header (e.g. login/register). */
  skipAuth?: boolean;
}

function buildUrl(
  path: string,
  params?: RequestOptions['params'],
): string {
  const url = new URL(
    path.startsWith('http') ? path : `${config.apiUrl}${path}`,
  );
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.append(key, String(value));
      }
    });
  }
  return url.toString();
}

/**
 * Thin typed fetch wrapper that injects auth, serialises JSON, and normalises
 * errors into `ApiError`. On a 401 it clears the stored token so guards can
 * redirect to login.
 */
export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, params, skipAuth } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = getAuthToken();
  if (token && !skipAuth) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(buildUrl(path, params), {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (response.status === 401) {
    clearAuthToken();
  }

  const isJson = response.headers
    .get('content-type')
    ?.includes('application/json');
  const payload = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const message =
      (isJson && (payload as { message?: string | string[] })?.message) ||
      response.statusText ||
      'Request failed';
    throw new ApiError(
      Array.isArray(message) ? message.join(', ') : String(message),
      response.status,
    );
  }

  return payload as T;
}
